import React, { useState } from 'react';
import { getRegionalUtilizationReport } from './api'; // Import the API function
import { transformData, getSortedPeriodStartDates } from './utils'; // Import utility functions
import UtilizationPivotTable from './UtilizationPivotTable'; // Import pivot table
import styled from 'styled-components';
import { FacilityData } from './types'; // Import types

// Styled-components for better styling
const Container = styled.div`
  width: 100%;
  margin-bottom: 70px;
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 20px;
  text-align: center;
  color: #333;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const SelectWrapper = styled.div`
  display: inline-block;
  align-items: center;
  margin-right: 20px;
`;

const Label = styled.label`
  display: inline-block;
  font-size: 14px;
  color: #333;
  margin-right: 10px;
`;

const Select = styled.select`
  display: inline-block;
  padding: 10px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  max-width: 300px;
`;

const Button = styled.button`
  display: inline-block;
  padding: 10px 20px;
  font-size: 16px;
  color: #fff;
  background-color: #007bff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 19px;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ExportButton = styled(Button)`
  margin-top: 0; /* Remove extra margin for the export button */
  margin-left: 20px; /* Add space between buttons */
`;

const Loading = styled.div`
  text-align: center;
  font-size: 18px;
  color: #666;
`;

const ErrorMessage = styled.div`
  color: black;
  font-size: 18px;
  text-align: center;
  margin-top: 10px;
`;

const Message = styled.div`
  margin-top: 20px;
  font-size: 16px;
  text-align: center;
  color: #333;
`;

// Helper function to generate quarters for the last 3 years
const getLastThreeYearsQuarters = () => {
  const currentYear = new Date().getFullYear();
  const quarters = [];

  for (let year = currentYear; year >= currentYear - 2; year--) {
    for (let quarter = 4; quarter >= 1; quarter--) {
      quarters.push(`${year} Q${quarter}`);
    }
  }

  return quarters;
};

// Helper function to convert a quarter to start and end dates
const convertQuarterToDateRange = (quarter: string) => {
  const [year, q] = quarter.split(' Q');
  const startEndDates: Record<string, { start: string; end: string }> = {
    '1': { start: `${year}-01-01`, end: `${year}-03-31` },
    '2': { start: `${year}-04-01`, end: `${year}-06-30` },
    '3': { start: `${year}-07-01`, end: `${year}-09-30` },
    '4': { start: `${year}-10-01`, end: `${year}-12-31` },
  };

  return startEndDates[q];
};

const convertDataToCSV = (data: FacilityData, periodStartDates: string[]) => {
  const rows = [];

  // Add header row
  const headerRow = ['Facility', 'Occupation', ...periodStartDates];
  rows.push(headerRow.join(','));

  // Add data rows
  Object.entries(data).forEach(([facility, occupations]) => {
    Object.entries(occupations).forEach(([occupation, periods]) => {
      const row = [facility, occupation];
      periodStartDates.forEach((date) => {
        if (periods === undefined) {
          row.push('0');
        } else {
          if (periods[date] !== undefined && periods[date] !== null) {
            const periodDate = periods[date];
            if (!periodDate) {
              row.push('0');
            } else {
              row.push(periodDate.toString());
            }
          } else {
            row.push('0');
          }
        }
      });
      rows.push(row.join(','));
    });
  });

  return rows.join('\n');
};

// Function to download the CSV file
const downloadCSV = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const RegionalUtilizationReport: React.FC = () => {
  const [preparedData, setPreparedData] = useState<FacilityData>({});
  const [sortedDates, setSortedDates] = useState<string[]>([]);
  const [loading, setLoading] = useState(false); // Set initial loading to false
  const [error, setError] = useState<string | null>(null);
  const [selectedQuarter, setSelectedQuarter] = useState<string | null>(null); // Track selected quarter
  const [aggregation, setAggregation] = useState<string>('week'); // Track selected aggregation (week or month)
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [lastGeneratedAggregation, setLastGeneratedAggregation] = useState<string>('week'); // Track aggregation at the time of last report generation

  const quarters = getLastThreeYearsQuarters(); // Generate quarter options

  // Fetch data from the API based on the selected quarter's date range and aggregation
  const fetchData = async (startDate: string, endDate: string, aggregation: string) => {
    try {
      setError(null);
      setLoading(true);
      const response = await getRegionalUtilizationReport(startDate, endDate, aggregation); // Pass start, end dates, and aggregation to the API
      const reportData = response.result;

      if (!reportData) {
        throw new Error('No data available for the selected quarter');
      }

      // Transform the data
      const data = transformData(reportData);
      const dates = getSortedPeriodStartDates(reportData);

      // Update state with transformed data and sorted dates
      setPreparedData(data as FacilityData);
      setSortedDates(dates);
      setStartDate(startDate); // Store start date
      setEndDate(endDate); // Store end date
      setLastGeneratedAggregation(aggregation); // Store the aggregation used when the report was generated
      setLoading(false);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message); // If the error is an instance of Error, use the message
      } else {
        setError('An unexpected error occurred'); // Fallback for unknown error types
      }
      setLoading(false);
    }
  };

  // Function to handle the "Generate Report" button click
  const handleGenerateReport = () => {
    if (selectedQuarter) {
      const { start, end } = convertQuarterToDateRange(selectedQuarter); // Convert selected quarter to date range
      fetchData(start, end, aggregation); // Fetch data based on start, end dates, and aggregation
    }
  };

  // Handle quarter selection change
  const handleQuarterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedQuarter(e.target.value);
  };

  // Handle aggregation selection change
  const handleAggregationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAggregation(e.target.value);
  };

  // Handle the export of the table data to CSV
  const handleExport = () => {
    if (preparedData && sortedDates.length > 0) {
      const csvContent = convertDataToCSV(preparedData, sortedDates);
      downloadCSV(csvContent, `regional_utilization_report_${new Date().toISOString()}.csv`);
    }
  };

  return (
    <Container>
      <Title>Regional Utilization Report</Title>

      {/* Dropdown to select quarter */}
      <FormGroup>
        <SelectWrapper>
          <Label htmlFor="quarter-select">Select Quarter:</Label>
          <Select id="quarter-select" onChange={handleQuarterChange} value={selectedQuarter || ''}>
            <option value="">-- Select a Quarter --</option>
            {quarters.map((quarter) => (
              <option key={quarter} value={quarter}>
                {quarter}
              </option>
            ))}
          </Select>
        </SelectWrapper>
        <SelectWrapper>
          <Label htmlFor="aggregation-select">Time Aggregation:</Label>
          <Select id="aggregation-select" onChange={handleAggregationChange} value={aggregation}>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </Select>
        </SelectWrapper>

        <Button onClick={handleGenerateReport} disabled={!selectedQuarter}>
          Generate Report <i className="fa fa-table" style={{ marginLeft: '3px' }}></i>
        </Button>

        {!loading && !error && preparedData && sortedDates.length > 0 && (
          <ExportButton onClick={handleExport} disabled={!preparedData || sortedDates.length === 0}>
            Export as CSV <i className="fa fa-download"></i>
          </ExportButton>
        )}
      </FormGroup>

      {/* Loading state */}
      {loading && <Loading>Loading...</Loading>}

      {/* Error state */}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* Render the pivot table when data is ready */}
      {!loading && !error && preparedData && sortedDates.length > 0 && (
        <>
          <UtilizationPivotTable data={preparedData} periodStartDates={sortedDates} />
          {/* Message below the table */}
          <Message>
            Data is displayed by {lastGeneratedAggregation}. The selected quarter's date range is from {startDate} to{' '}
            {endDate}.
          </Message>
        </>
      )}
    </Container>
  );
};

export default RegionalUtilizationReport;
